/**
 * The sign-in cooldown's deadline, as the one thing the browser remembers about it.
 *
 * **A DEADLINE, never a countdown.** Storing "47 seconds left" means the number is wrong the
 * moment the tab is backgrounded, reloaded or restored, and every one of those makes the wait
 * run long or start over. Storing the instant it ends makes the display a function of the
 * clock, so there is no state to keep in step.
 *
 * It lives in `sessionStorage` because it is per TAB and per visit: closing the browser ends
 * it, which is correct, since this is only ever a convenience. **The server is the control** —
 * `AuthService` counts the window off `LoginAttempts` and refuses the request whether or not
 * anything here survives. Clearing this storage buys nobody an earlier sign-in.
 */
const KEY = 'rsys.signin-cooldown-until';

/** Records when the wait ends, from the seconds the server asked for. */
export function startCooldown(seconds: number): void {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return;
  }

  try {
    sessionStorage.setItem(KEY, String(Date.now() + seconds * 1000));
  } catch {
    // Private mode, or storage blocked. The page still draws from the value it was given on
    // arrival; only surviving a reload is lost, and the server refuses either way.
  }
}

/** Whole seconds still to wait, or 0 when there is nothing pending. */
export function cooldownRemaining(): number {
  let stored: string | null = null;

  try {
    stored = sessionStorage.getItem(KEY);
  } catch {
    return 0;
  }

  if (stored === null) {
    return 0;
  }

  const until = Number(stored);

  // A value from an older shape, or one somebody edited. Unreadable means not waiting.
  if (!Number.isFinite(until)) {
    return 0;
  }

  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

export function clearCooldown(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clean up that anything reads.
  }
}
